/**
 * Currency Formatter
 * 
 * Handles currency formatting with proper symbols and decimal/thousand separators.
 * 
 * @package betbeast
 */

export class CurrencyFormatter {
    constructor() {
        this.currencyConfig = {
            'EUR': { symbol: '€', decimals: 2, thousandSep: '.', decimalSep: ',', symbolPosition: 'before' },
            'USD': { symbol: '$', decimals: 2, thousandSep: ',', decimalSep: '.', symbolPosition: 'before' },
            'GBP': { symbol: '£', decimals: 2, thousandSep: ',', decimalSep: '.', symbolPosition: 'before' },
            'CAD': { symbol: 'CA$', decimals: 2, thousandSep: ',', decimalSep: '.', symbolPosition: 'before' },
            'AUD': { symbol: 'A$', decimals: 2, thousandSep: ',', decimalSep: '.', symbolPosition: 'before' },
            'NZD': { symbol: 'NZ$', decimals: 2, thousandSep: ',', decimalSep: '.', symbolPosition: 'before' },
            'PLN': { symbol: 'zł', decimals: 2, thousandSep: ' ', decimalSep: ',', symbolPosition: 'after' },
            'BRL': { symbol: 'R$', decimals: 2, thousandSep: '.', decimalSep: ',', symbolPosition: 'before' },
            'NOK': { symbol: 'kr', decimals: 2, thousandSep: ' ', decimalSep: ',', symbolPosition: 'after' },
            'ZAR': { symbol: 'R', decimals: 2, thousandSep: ' ', decimalSep: '.', symbolPosition: 'before' },
            'RUB': { symbol: '₽', decimals: 2, thousandSep: ' ', decimalSep: ',', symbolPosition: 'after' },
            'INR': { symbol: '₹', decimals: 2, thousandSep: ',', decimalSep: '.', symbolPosition: 'before' },
            'JPY': { symbol: '¥', decimals: 0, thousandSep: ',', decimalSep: '.', symbolPosition: 'before' },
        };
    }
    
    /**
     * Format amount with currency symbol and proper separators
     * 
     * @param {number} amount Amount to format
     * @param {string} currencyCode Currency code (e.g., EUR, USD)
     * @param {boolean} includeSymbol Include currency symbol
     * @returns {string} Formatted amount
     */
    format(amount, currencyCode, includeSymbol = true) {
        const config = this.currencyConfig[currencyCode] || this.currencyConfig['EUR'];
        
        // Round to proper decimal places
        const roundedAmount = parseFloat(amount).toFixed(config.decimals);
        
        // Split into integer and decimal parts
        const [integerPart, decimalPart] = roundedAmount.split('.');
        
        // Add thousand separators
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, config.thousandSep);
        
        // Combine with decimal separator - only show decimals if they're not .00
        let formatted = formattedInteger;
        if (config.decimals > 0 && decimalPart && decimalPart !== '00') {
            formatted += config.decimalSep + decimalPart;
        }
        
        // Add currency symbol
        if (includeSymbol) {
            if (config.symbolPosition === 'before') {
                formatted = config.symbol + formatted;
            } else {
                formatted = formatted + ' ' + config.symbol;
            }
        }
        
        return formatted;
    }
    
    /**
     * Get currency symbol
     * 
     * @param {string} currencyCode Currency code
     * @returns {string} Currency symbol
     */
    getSymbol(currencyCode) {
        const config = this.currencyConfig[currencyCode] || this.currencyConfig['EUR'];
        return config.symbol;
    }
    
    /**
     * Get currency configuration
     * 
     * @param {string} currencyCode Currency code
     * @returns {Object} Currency config
     */
    getConfig(currencyCode) {
        return this.currencyConfig[currencyCode] || this.currencyConfig['EUR'];
    }
    
    /**
     * Parse formatted currency string back to number
     * 
     * @param {string} formattedAmount Formatted currency string
     * @param {string} currencyCode Currency code
     * @returns {number} Numeric value
     */
    parse(formattedAmount, currencyCode) {
        const config = this.currencyConfig[currencyCode] || this.currencyConfig['EUR'];
        
        // Remove currency symbol
        let cleaned = formattedAmount.replace(config.symbol, '').trim();
        
        // Remove thousand separators
        cleaned = cleaned.split(config.thousandSep).join('');
        
        // Replace decimal separator with dot
        cleaned = cleaned.replace(config.decimalSep, '.');
        
        return parseFloat(cleaned) || 0;
    }
}
